import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/DropdownMenu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/Avatar";
import { PanelLeft, Menu, MessageSquare, LogOut, LayoutGrid } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { getPageTitle } from "@/utils/pageTitle";
import { useProfile } from "@/hooks/useProfile";
import { getInitials } from "@/utils/util";
import { getPrimaryRole, isSuperAdmin } from "@/utils/roleUtils";
import { AvatarSkeleton } from "@/components/skeletons";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationPanel } from "@/components/NotificationPanel";
import { useUnreadMessageCount } from "@/hooks/useNotifications";

const MessagesButton = ({ onClick }: { onClick: () => void }) => {
  const { data } = useUnreadMessageCount();
  const count = data?.unreadCount ?? 0;

  return (
    <button
      className="relative flex h-8 w-8 items-center justify-center rounded-full border border-theme-border bg-theme-bg-surface-raised hover:bg-theme-bg-hover transition-colors"
      title="Messages"
      onClick={onClick}
    >
      <MessageSquare className="h-4 w-4 text-theme-text-secondary" />
      {count > 0 && (
        <span className="absolute -right-1 -top-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[var(--color-primary-750)] px-1 text-[10px] font-bold text-white leading-none">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
};

interface HeaderProps {
  onToggleSidebar?: () => void;
  onHamburgerClick?: () => void;
  onMessagesClick?: () => void;
  onCalendarClick?: () => void;
  pageTitle?: string;
  isSidebarCollapsed?: boolean;
}

// DISABLED (multi-account): rendered each switcher row's role label.
// function roleLabel(slot: AccountSlot): string {
//   const primary = slot.roles.find((r) => r !== "admin" && r !== "super_admin") ?? slot.role;
//   return primary.charAt(0).toUpperCase() + primary.slice(1);
// }

export const Header = ({
  onToggleSidebar,
  onHamburgerClick,
  onMessagesClick,
  pageTitle,
  isSidebarCollapsed = false,
}: HeaderProps) => {
  const { user, loading, logout } = useAuth();

  const { profile } = useProfile(user, !!user);
  const location = useLocation();
  const navigate = useNavigate();
  const primaryRole = user ? getPrimaryRole(user) : "";
  const userRole =
    primaryRole === "advisor" ? "Expert" : primaryRole === "achiever" ? "Achiever" : "";
  const desktopSidebarWidth = isSidebarCollapsed ? "md:w-16" : "md:w-60";
  const isHomePage = location.pathname === "/home";
  // Inside the Getting Started flow, a Home / Getting Started breadcrumb
  // takes the expert back to Home part-way through.
  const isGettingStarted = location.pathname === "/getting-started";
  // Every route is labelled: the sidebar item it belongs to, else a fallback
  // (see getPageTitle). An explicit `pageTitle` still wins.
  const workspace = useActiveWorkspace();
  const displayPageTitle = isHomePage
    ? "Home"
    : (pageTitle ?? getPageTitle(location.pathname, workspace));
  // DISABLED (multi-account): cross-tab account-link listener.
  // The new-account tab broadcast "account_added" on the fwd_account_added
  // channel and this effect refreshed the switcher in place. Nothing publishes
  // that message any more. (Sign-out still syncs across tabs — AuthContext
  // listens on AUTH_LOGOUT_CHANNEL.)
  //
  // const refreshRef = useRef(refreshConnectedAccounts);
  // refreshRef.current = refreshConnectedAccounts;
  //
  // const channelRef = useRef<BroadcastChannel | null>(null);
  // useEffect(() => {
  //   if (typeof BroadcastChannel !== "undefined") {
  //     const ch = new BroadcastChannel("fwd_account_added");
  //     channelRef.current = ch;
  //     ch.onmessage = () => refreshRef.current();
  //   }
  //
  //   return () => {
  //     channelRef.current?.close();
  //   };
  // }, []);
  //
  // const handleAddAccount = () => {
  //   addAccount();
  // };

  return (
    <div className="h-[var(--layout-top-header-height)] flex">
      {/* Left Section */}
      <div
        className={`w-14 ${desktopSidebarWidth} h-full flex items-center gap-3 px-2 md:px-4 border-r bg-theme-bg-header border-b border-theme-border transition-all duration-300 shrink-0`}
      >
        {!!onHamburgerClick && (
          <button
            onClick={onHamburgerClick}
            className="md:hidden p-2 text-theme-text-secondary hover:text-theme-text hover:bg-theme-bg-surface-raised rounded-md transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        {!!onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="hidden md:flex p-2 text-theme-text-secondary hover:text-theme-text hover:bg-theme-bg-surface-raised rounded-md transition-colors"
            aria-label="Toggle sidebar"
          >
            <PanelLeft className="w-5 h-5" />
          </button>
        )}
        {!isSidebarCollapsed && (
          <>
            {/* 155x36 — the internal-screen logo size, smaller than the 218x50
                the auth/signup screens use. min-w-0 + max-w-full let
                object-contain scale it down rather than clip: this column is
                only md:w-60 (240px) less px-4 and the toggle button beside it,
                and without them the flex item was cut off at the right edge. */}
            <img
              src={fsvLogo}
              alt="AchieveOS Logo"
              className="hidden md:block h-[36px] w-[155px] min-w-0 max-w-full object-contain object-left dark:hidden"
            />
            <img
              src={fsvLogoDark}
              alt="AchieveOS Logo"
              className="hidden md:dark:block h-[36px] w-[155px] min-w-0 max-w-full object-contain object-left"
            />
          </>
        )}
      </div>

      {/* Right Section */}
      <div className="flex-1 min-w-0 h-full bg-theme-bg-header border-b border-theme-border shadow-sm flex items-center px-3 sm:px-4 md:px-6 py-4">
        {isGettingStarted ? (
          // Sizes set on each element, not the <nav>: the global h1 style
          // would otherwise override an inherited size and blow it up.
          <nav aria-label="Breadcrumb" className="mr-2 flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="shrink-0 font-inter text-[15px] font-medium leading-[22.5px] text-theme-text-muted transition-colors hover:text-theme-text-heading"
            >
              Home
            </button>
            <span
              aria-hidden
              className="font-inter text-[15px] font-medium leading-[22.5px] text-theme-text-muted"
            >
              /
            </span>
            <h1
              aria-current="page"
              className="m-0 truncate font-inter text-[15px] font-medium leading-[22.5px] text-theme-text-muted"
            >
              Getting Started
            </h1>
          </nav>
        ) : (
          displayPageTitle && (
            <h1 className="text-base sm:text-lg md:text-20 font-normal text-theme-text-heading truncate mr-2">
              {displayPageTitle}
            </h1>
          )
        )}

        <div className="flex items-center gap-1 sm:gap-2 ml-auto shrink-0">
          <ThemeToggle />
          {/* Goals icon removed from header — goals are accessible via the left nav (achievers) and PAB details page (advisors) */}
          {/* {showGoalsButton && !!onGoalsClick && (
            <button
              className="flex h-8 w-8 items-center justify-center rounded-full border border-theme-border bg-theme-bg-surface-raised hover:bg-theme-bg-hover transition-colors"
              title="Goals"
              onClick={onGoalsClick || onHamburgerClick}
            >
              <Target className="h-4 w-4 text-theme-text-secondary" />
            </button>
          )} */}

          <NotificationPanel />

          {!!onMessagesClick && <MessagesButton onClick={onMessagesClick} />}

          {/* User Profile + Account Switcher */}
          {loading || !user ? (
            <div className="flex items-center gap-2 px-2 py-1">
              <AvatarSkeleton size={24} withLabel labelWidth="80px" />
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-tour-id="nav-avatar"
                  className="flex items-center gap-1 rounded-full border border-theme-border bg-theme-bg-surface px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-theme-bg-surface-raised transition-colors min-w-0"
                >
                  <Avatar className="h-7 w-7 sm:h-8 sm:w-8 border-0 shrink-0">
                    {profile?.profilePictureUrl ? (
                      <AvatarImage
                        src={profile.profilePictureUrl as string}
                        alt={user.fullName}
                        className="grayscale"
                      />
                    ) : null}
                    <AvatarFallback className="text-10 sm:text-12 font-bold text-neutral-dark bg-header-gradient from-purple-500 to-pink-500">
                      {getInitials(user.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:flex flex-col items-start min-w-0 max-w-[100px] md:max-w-[140px]">
                    <span className="text-12 font-medium text-theme-text-heading leading-tight truncate w-full">
                      {user.fullName}
                    </span>
                    {userRole && (
                      <span className="text-10 text-advisory-card-value leading-tight">
                        {userRole}
                      </span>
                    )}
                  </div>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="w-80 bg-theme-bg-surface text-theme-text border border-theme-border shadow-lg py-2 px-2 flex flex-col max-h-[min(520px,80vh)] overflow-hidden"
              >
                {/* ── Section label ── */}
                <DropdownMenuLabel
                  className="px-1 pb-2 text-xs font-semibold uppercase tracking-[0.6px] shrink-0"
                  style={{ color: "var(--advisory-style-card-value)" }}
                >
                  Account
                </DropdownMenuLabel>

                {/* ── Signed-in user ── */}
                {(() => {
                  // Avatar keyed by url so Radix remounts it when the src changes.
                  const pictureUrl =
                    (profile?.profilePictureUrl as string | undefined)?.trim() || null;
                  const avatarKey = `${user.id}-${pictureUrl ?? "none"}`;

                  return (
                    <DropdownMenuItem
                      key={avatarKey}
                      onClick={() => navigate("/profile")}
                      className="flex items-center gap-3 rounded-[14px] border px-3 py-3 min-h-[66px] mb-2 outline-none cursor-pointer bg-[var(--theme-accent-subtle)] border-[var(--theme-border-subtle)] hover:bg-[var(--theme-bg-hover)]"
                    >
                      <Avatar className="h-10 w-10 shrink-0 self-center">
                        {pictureUrl ? <AvatarImage src={pictureUrl} alt={user.fullName} /> : null}
                        <AvatarFallback className="text-xs font-bold text-neutral-dark bg-header-gradient from-purple-500 to-pink-500">
                          {getInitials(user.fullName || user.email)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex flex-col min-w-0 flex-1">
                        <p
                          className="text-sm font-medium leading-5 tracking-[-0.15px] truncate"
                          style={{ color: "var(--color-timeline-activity-card-text)" }}
                        >
                          {user.fullName || user.email}
                        </p>
                        <p
                          className="text-xs font-normal leading-4 truncate"
                          style={{ color: "var(--advisory-style-card-value)" }}
                        >
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  );
                })()}

                {/* ── Footer ── */}
                <div className="shrink-0">
                  {/* <DropdownMenuSeparator /> */}

                  {/* Admin panel — only for super admins. AdminGuard and every
                      /admin endpoint re-check the role, so hiding this item is
                      convenience, not the access control. */}
                  {isSuperAdmin(user) && (
                    <DropdownMenuItem
                      onClick={() => navigate("/admin")}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer text-13 rounded-md hover:bg-[var(--theme-bg-hover)]"
                      style={{ color: "var(--theme-text)" }}
                    >
                      <LayoutGrid
                        className="h-4 w-4 shrink-0"
                        style={{ color: "var(--theme-text-secondary)" }}
                      />
                      Super Admin Panel
                    </DropdownMenuItem>
                  )}

                  {/* Sign out active account */}
                  <DropdownMenuItem
                    onClick={logout}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer text-13 rounded-md hover:bg-[var(--theme-bg-hover)]"
                    style={{ color: "var(--theme-text)" }}
                  >
                    <LogOut
                      className="h-4 w-4 shrink-0"
                      style={{ color: "var(--theme-text-secondary)" }}
                    />
                    Logout
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
};
